import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, Eye, EyeOff, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export const ResetPasswordPage = () => {
  const { user, loading, changePassword } = useAuth();
  const [form, setForm]     = useState({ next: "", confirm: "" });
  const [show, setShow]     = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone]     = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6)       { toast.error("New password must be at least 6 characters."); return; }
    if (form.next !== form.confirm) { toast.error("Passwords do not match."); return; }
    setSaving(true);
    try {
      await changePassword(null, form.next);
      setDone(true);
      toast.success("Password updated successfully.");
    } catch (err) {
      toast.error(err?.message || "Could not update password. Please request a new reset link.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 bg-[#F5E6CC] py-14">
      <div className="bg-white/60 border border-[#5A2D1D]/20 rounded-sm p-8 max-w-sm w-full">
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-5 h-5 text-[#556B2F]" />
          <h1 className="font-display text-xl font-bold text-[#5A2D1D]">Reset Password</h1>
        </div>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#5A2D1D] mx-auto" />
          </div>
        ) : done ? (
          <div className="py-4">
            <div className="flex items-center gap-2 text-sm text-[#556B2F] font-semibold bg-[#556B2F]/10 border border-[#556B2F]/30 rounded-sm px-3 py-2 mb-4">
              <CheckCircle className="w-4 h-4" /> Password updated. You can sign in now.
            </div>
            <Link to="/" className="inline-block bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors">
              Back to Home
            </Link>
          </div>
        ) : !user ? (
          <div className="py-4">
            <p className="text-sm text-[#2E1A12]/70 mt-3 mb-4">
              This password reset link is invalid or has expired. Please request a new one from the sign-in screen.
            </p>
            <Link to="/" className="inline-block bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors">
              Back to Home
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-[#2E1A12]/60 mt-1 mb-5">Choose a new password for {user.email}.</p>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    value={form.next}
                    onChange={set("next")}
                    autoFocus
                    required
                    className="w-full px-3 py-2.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12] pr-10"
                  />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A2D1D]/50 hover:text-[#5A2D1D]">
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1">Confirm New Password</label>
                <input
                  type={show ? "text" : "password"}
                  value={form.confirm}
                  onChange={set("confirm")}
                  required
                  className="w-full px-3 py-2.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
                />
              </div>
              <button
                type="submit"
                disabled={saving || !form.next || !form.confirm}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] px-5 py-2.5 rounded-full font-semibold text-sm transition-colors"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Update Password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
